import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Stack,
  Group,
  Text,
  Button,
  Box,
  Collapse,
  ActionIcon,
  Select,
  Card,
  Divider,
  Center,
  Loader,
  CopyButton,
  Tooltip,
  Modal,
  Alert,
  Badge,
} from '@mantine/core';
import {
  Clock,
  ChevronDown,
  ChevronUp,
  Edit2,
  Info,
  Plus,
  Copy,
  Check,
  Trash2,
  AlertTriangle,
  RotateCcw,
} from 'react-feather';
import { TimeField, TimezoneSelect } from '../../../components/common';
import { getTimezoneAbbreviation } from '../../../utils/timezone';
import { notifications } from '@mantine/notifications';
import { timeSlotsApi } from '../../../services/api/timeSlots';
import type {
  TimeSlotResponse,
  CreateTimeSlotRequest,
  DayOfWeek,
} from '../../../types';
import type { SectionRegister } from './edit-cohort/sectionContract';

// Days of week options for select
const DAY_OPTIONS = [
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
];

interface TimeSlotSectionProps {
  cohortId: string;
  cohortCode: string | null;
  isCollapsible?: boolean;
  defaultExpanded?: boolean;
  /** MED-1269: register this section's deferred-save controller with the modal.
   * When provided, add/edit/delete are staged locally and committed on Save. */
  registerSection?: SectionRegister;
}

interface TimeSlotFormData {
  day_of_week: DayOfWeek;
  time: string;
  timezone: string;
}

/** A slot in the working (pending) list. New additions carry `serverId: null`;
 * `pendingDelete` marks an existing slot staged for deletion (still shown,
 * undoable - AC17). `baseline` is the as-loaded snapshot for edit-diffing. */
interface WorkingSlot {
  localId: string;
  serverId: string | null;
  day_of_week: DayOfWeek;
  time: string; // HH:MM
  timezone: string;
  identifier: string | null;
  pendingDelete: boolean;
  baseline: { day_of_week: DayOfWeek; time: string; timezone: string } | null;
}

const defaultFormData: TimeSlotFormData = {
  day_of_week: 'monday',
  time: '09:00',
  timezone: 'America/New_York',
};

let localIdCounter = 0;
const nextLocalId = () => `new-${++localIdCounter}`;

const normTime = (t: string) => (t.length >= 5 ? t.substring(0, 5) : t);

const toWorkingSlot = (slot: TimeSlotResponse): WorkingSlot => ({
  localId: slot.id,
  serverId: slot.id,
  day_of_week: slot.day_of_week,
  time: normTime(slot.time),
  timezone: slot.timezone,
  identifier: slot.identifier,
  pendingDelete: false,
  baseline: {
    day_of_week: slot.day_of_week,
    time: normTime(slot.time),
    timezone: slot.timezone,
  },
});

const displayDay = (day: DayOfWeek) =>
  day.charAt(0).toUpperCase() + day.slice(1);

const displayTime = (time: string) => {
  const [h, m] = time.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${String(m).padStart(2, '0')} ${period}`;
};

const slotIsDirty = (s: WorkingSlot): boolean => {
  if (s.serverId === null) return true; // staged addition
  if (s.pendingDelete) return true; // staged deletion
  if (!s.baseline) return false;
  return (
    s.day_of_week !== s.baseline.day_of_week ||
    s.time !== s.baseline.time ||
    s.timezone !== s.baseline.timezone
  );
};

export function TimeSlotSection({
  cohortId,
  cohortCode,
  isCollapsible = true,
  defaultExpanded = true,
  registerSection,
}: TimeSlotSectionProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [slots, setSlots] = useState<WorkingSlot[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [addFormData, setAddFormData] =
    useState<TimeSlotFormData>(defaultFormData);
  const [editFormData, setEditFormData] =
    useState<TimeSlotFormData>(defaultFormData);
  // Delete modal state - the usage check still runs at click time (AC18);
  // the deletion itself is staged until Save.
  const [slotToDelete, setSlotToDelete] = useState<WorkingSlot | null>(null);
  const [deleteUsageInfo, setDeleteUsageInfo] = useState<{
    isAssignedToGroup: boolean;
    availabilityRequestCount: number;
    canDelete: boolean;
  } | null>(null);
  const [isLoadingUsage, setIsLoadingUsage] = useState(false);

  const loadTimeSlots = useCallback(async () => {
    try {
      setLoading(true);
      const response = await timeSlotsApi.list(cohortId);
      setSlots(response.time_slots.map(toWorkingSlot));
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to load time slots',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  }, [cohortId]);

  useEffect(() => {
    if (cohortId) loadTimeSlots();
  }, [cohortId, loadTimeSlots]);

  const handleAddTimeSlot = () => {
    if (!cohortCode) {
      notifications.show({
        title: 'Error',
        message: 'Cohort must have a code before adding time slots',
        color: 'red',
      });
      return;
    }
    // MED-1269: stage the addition locally; committed on footer Save.
    setSlots(prev => [
      ...prev,
      {
        localId: nextLocalId(),
        serverId: null,
        day_of_week: addFormData.day_of_week,
        time: normTime(addFormData.time),
        timezone: addFormData.timezone,
        identifier: null,
        pendingDelete: false,
        baseline: null,
      },
    ]);
    setShowAddForm(false);
    setAddFormData(defaultFormData);
  };

  const handleStartEdit = (slot: WorkingSlot) => {
    setEditingId(slot.localId);
    setEditFormData({
      day_of_week: slot.day_of_week,
      time: normTime(slot.time),
      timezone: slot.timezone,
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditFormData(defaultFormData);
  };

  // MED-1269 (Flag 2): commits the row edit into local state, not the server.
  const handleApplyEdit = () => {
    if (!editingId) return;
    setSlots(prev =>
      prev.map(s =>
        s.localId === editingId
          ? {
              ...s,
              day_of_week: editFormData.day_of_week,
              time: normTime(editFormData.time),
              timezone: editFormData.timezone,
            }
          : s
      )
    );
    setEditingId(null);
    setEditFormData(defaultFormData);
  };

  const handleDeleteClick = async (slot: WorkingSlot) => {
    // Staged additions can be dropped immediately - nothing on the server yet.
    if (slot.serverId === null) {
      setSlots(prev => prev.filter(s => s.localId !== slot.localId));
      return;
    }
    setSlotToDelete(slot);
    setIsLoadingUsage(true);
    setDeleteUsageInfo(null);
    try {
      // AC18: usage check still runs when the user initiates the delete.
      const usageInfo = await timeSlotsApi.getUsage(cohortId, slot.serverId);
      setDeleteUsageInfo(usageInfo);
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to load time slot usage information',
        color: 'red',
      });
      setSlotToDelete(null);
    } finally {
      setIsLoadingUsage(false);
    }
  };

  const handleCloseDeleteModal = () => {
    setSlotToDelete(null);
    setDeleteUsageInfo(null);
  };

  // Stage (not commit) the deletion, keeping the row visible + undoable (AC17).
  const handleStageDelete = () => {
    if (!slotToDelete) return;
    setSlots(prev =>
      prev.map(s =>
        s.localId === slotToDelete.localId ? { ...s, pendingDelete: true } : s
      )
    );
    handleCloseDeleteModal();
  };

  const handleUndoDelete = (localId: string) => {
    setSlots(prev =>
      prev.map(s =>
        s.localId === localId ? { ...s, pendingDelete: false } : s
      )
    );
  };

  // ---- Deferred-save controller (MED-1269) ----
  const slotsRef = useRef(slots);
  slotsRef.current = slots;

  const commit = useCallback(async () => {
    const working = slotsRef.current;
    // Deletions and updates need a server id; additions are the null-id rows.
    const creates = working.filter(
      s => s.serverId === null && !s.pendingDelete
    );
    const deletes = working.filter(s => s.serverId !== null && s.pendingDelete);
    const updates = working.filter(
      s => s.serverId !== null && !s.pendingDelete && slotIsDirty(s)
    );

    for (const s of creates) {
      const request: CreateTimeSlotRequest = {
        day_of_week: s.day_of_week,
        time: s.time.includes(':') ? `${normTime(s.time)}:00` : `${s.time}:00`,
        timezone: s.timezone,
      };
      await timeSlotsApi.create(cohortId, request);
    }
    for (const s of updates) {
      await timeSlotsApi.update(cohortId, s.serverId!, {
        day_of_week: s.day_of_week,
        time: `${normTime(s.time)}:00`,
        timezone: s.timezone,
      });
    }
    for (const s of deletes) {
      await timeSlotsApi.delete(cohortId, s.serverId!);
    }
    // Reconcile against server truth so identifiers/order reflect the save.
    await loadTimeSlots();
  }, [cohortId, loadTimeSlots]);

  useEffect(() => {
    if (!registerSection) return;
    const dirty = slots.some(slotIsDirty);
    const deletions = slots.filter(s => s.serverId !== null && s.pendingDelete);
    const destructive = deletions.map(s => ({
      label:
        `Delete time slot ${displayDay(s.day_of_week)} ${displayTime(s.time)} ${getTimezoneAbbreviation(s.timezone)}`.trim(),
    }));
    registerSection({ dirty, destructive, commit });
    return () => registerSection(null);
  }, [slots, registerSection, commit]);

  const visibleSlots = slots;

  const renderTimeSlotRow = (slot: WorkingSlot) => {
    if (editingId === slot.localId) {
      return (
        <Box p="sm" style={{ backgroundColor: 'var(--mantine-color-gray-0)' }}>
          <Group gap="sm" align="flex-end">
            <Select
              label="Day"
              data={DAY_OPTIONS}
              value={editFormData.day_of_week}
              onChange={value =>
                setEditFormData(prev => ({
                  ...prev,
                  day_of_week: value as DayOfWeek,
                }))
              }
              style={{ flex: 1 }}
              size="sm"
            />
            <TimeField
              label="Time"
              value={editFormData.time}
              onChange={value =>
                setEditFormData(prev => ({ ...prev, time: value }))
              }
              style={{ flex: 1 }}
              size="sm"
            />
            <TimezoneSelect
              label="Timezone"
              value={editFormData.timezone}
              onChange={value =>
                setEditFormData(prev => ({
                  ...prev,
                  timezone: value || 'America/New_York',
                }))
              }
              style={{ flex: 1.5 }}
              size="sm"
            />
          </Group>
          <Group justify="flex-end" mt="sm">
            <Button variant="default" size="xs" onClick={handleCancelEdit}>
              Cancel
            </Button>
            {/* Flag 2: this commits to local state, not the server - labelled Done. */}
            <Button size="xs" onClick={handleApplyEdit}>
              Done
            </Button>
          </Group>
        </Box>
      );
    }

    const isStaged = slot.serverId === null;
    const isPendingDelete = slot.pendingDelete;

    return (
      <Group
        justify="space-between"
        p="sm"
        style={isPendingDelete ? { opacity: 0.55 } : undefined}
      >
        <Group gap="xl">
          <Text
            size="sm"
            fw={500}
            w={100}
            style={
              isPendingDelete ? { textDecoration: 'line-through' } : undefined
            }
          >
            {slot.identifier ?? '-'}
          </Text>
          <Text
            size="sm"
            w={100}
            style={
              isPendingDelete ? { textDecoration: 'line-through' } : undefined
            }
          >
            {displayDay(slot.day_of_week)}
          </Text>
          <Text
            size="sm"
            style={
              isPendingDelete ? { textDecoration: 'line-through' } : undefined
            }
          >
            {displayTime(slot.time)} {getTimezoneAbbreviation(slot.timezone)}
          </Text>
          {isStaged && !isPendingDelete && (
            <Badge size="xs" variant="light" color="blue">
              New - not saved
            </Badge>
          )}
          {isPendingDelete && (
            <Badge size="xs" variant="light" color="red">
              Will be deleted on save
            </Badge>
          )}
        </Group>
        <Group gap="xs">
          {isPendingDelete ? (
            <Tooltip label="Undo delete" withArrow>
              <ActionIcon
                variant="subtle"
                size="sm"
                onClick={() => handleUndoDelete(slot.localId)}
              >
                <RotateCcw size={14} />
              </ActionIcon>
            </Tooltip>
          ) : (
            <>
              <ActionIcon
                variant="subtle"
                size="sm"
                onClick={() => handleStartEdit(slot)}
              >
                <Edit2 size={14} />
              </ActionIcon>
              <ActionIcon
                variant="subtle"
                size="sm"
                color="red"
                onClick={() => handleDeleteClick(slot)}
              >
                <Trash2 size={14} />
              </ActionIcon>
            </>
          )}
        </Group>
      </Group>
    );
  };

  const renderAddForm = () => (
    <Box
      p="sm"
      style={{
        backgroundColor: 'var(--mantine-color-gray-0)',
        borderRadius: 6,
      }}
    >
      <Group gap="sm" align="flex-end">
        <Select
          label="Day"
          data={DAY_OPTIONS}
          value={addFormData.day_of_week}
          onChange={value =>
            setAddFormData(prev => ({
              ...prev,
              day_of_week: value as DayOfWeek,
            }))
          }
          style={{ flex: 1 }}
          size="sm"
        />
        <TimeField
          label="Time"
          value={addFormData.time}
          onChange={value => setAddFormData(prev => ({ ...prev, time: value }))}
          style={{ flex: 1 }}
          size="sm"
        />
        <TimezoneSelect
          label="Timezone"
          value={addFormData.timezone}
          onChange={value =>
            setAddFormData(prev => ({
              ...prev,
              timezone: value || 'America/New_York',
            }))
          }
          style={{ flex: 1.5 }}
          size="sm"
        />
      </Group>
      <Group justify="flex-end" mt="sm">
        <Button
          variant="default"
          size="xs"
          onClick={() => setShowAddForm(false)}
        >
          Cancel
        </Button>
        <Button size="xs" onClick={handleAddTimeSlot}>
          Add
        </Button>
      </Group>
    </Box>
  );

  // For the Paperform copy, only slots that will actually exist after save.
  const persistedSlots = visibleSlots.filter(s => !s.pendingDelete);

  const content = (
    <Stack gap="md">
      <Text size="sm" c="dimmed">
        Define available meeting times for group coaching sessions. Participants
        and coaches will indicate their availability from these options. Changes
        are saved when you click Save Changes.
      </Text>

      {loading && (
        <Center py="md">
          <Loader size="sm" />
        </Center>
      )}

      {!loading && visibleSlots.length > 0 ? (
        <Card withBorder p={0}>
          <Stack gap={0}>
            {visibleSlots.map((slot, index) => (
              <Box key={slot.localId}>
                {renderTimeSlotRow(slot)}
                {index < visibleSlots.length - 1 && <Divider />}
              </Box>
            ))}
          </Stack>
        </Card>
      ) : !loading && !showAddForm ? (
        <Card withBorder p="xl" style={{ borderStyle: 'dashed' }}>
          <Stack align="center" gap="xs">
            <Button
              variant="subtle"
              leftSection={<Plus size={16} />}
              onClick={() => setShowAddForm(true)}
              disabled={!cohortCode}
            >
              Add Time Slot
            </Button>
          </Stack>
        </Card>
      ) : null}

      {showAddForm && renderAddForm()}

      {visibleSlots.length > 0 && !showAddForm && (
        <Button
          variant="outline"
          leftSection={<Plus size={16} />}
          onClick={() => setShowAddForm(true)}
          disabled={!cohortCode}
        >
          Add Time Slot
        </Button>
      )}

      {persistedSlots.length > 0 && (
        <Card
          withBorder
          p="sm"
          style={{ backgroundColor: 'var(--mantine-color-gray-0)' }}
        >
          <Stack gap="xs">
            <Group gap="xs">
              <Info size={14} color="var(--mantine-color-gray-6)" />
              <Text size="sm" fw={500}>
                Copy for Paperform
              </Text>
            </Group>
            <Text size="sm" c="dimmed">
              Number options in Paperform to match this order:
            </Text>
            <Box
              p="xs"
              style={{
                backgroundColor: 'var(--mantine-color-gray-1)',
                borderRadius: 4,
                fontFamily: 'monospace',
              }}
            >
              <Text size="sm">
                {persistedSlots
                  .map(
                    (s, i) =>
                      `${i + 1}. ${displayDay(s.day_of_week)} ${displayTime(s.time)} ${getTimezoneAbbreviation(s.timezone)}`
                  )
                  .join(', ')}
              </Text>
            </Box>
            <CopyButton
              value={persistedSlots
                .map(
                  (s, i) =>
                    `${i + 1}. ${displayDay(s.day_of_week)} ${displayTime(s.time)} ${getTimezoneAbbreviation(s.timezone)}`
                )
                .join(', ')}
            >
              {({ copied, copy }) => (
                <Tooltip
                  label={copied ? 'Copied!' : 'Copy to clipboard'}
                  withArrow
                >
                  <Button
                    variant={copied ? 'filled' : 'outline'}
                    color={copied ? 'green' : 'gray'}
                    size="xs"
                    leftSection={
                      copied ? <Check size={14} /> : <Copy size={14} />
                    }
                    onClick={() => {
                      copy();
                      notifications.show({
                        title: 'Copied!',
                        message: 'Time slot options copied to clipboard',
                        color: 'green',
                      });
                    }}
                  >
                    {copied ? 'Copied' : 'Copy to Clipboard'}
                  </Button>
                </Tooltip>
              )}
            </CopyButton>
          </Stack>
        </Card>
      )}
    </Stack>
  );

  const deleteModal = (
    <Modal
      opened={!!slotToDelete}
      onClose={handleCloseDeleteModal}
      title="Delete Time Slot?"
      centered
    >
      {isLoadingUsage ? (
        <Center py="xl">
          <Loader size="sm" />
        </Center>
      ) : deleteUsageInfo ? (
        <Stack gap="md">
          <Text size="sm">
            Are you sure you want to delete{' '}
            <Text span fw={600}>
              {slotToDelete &&
                `${displayDay(slotToDelete.day_of_week)} - ${displayTime(slotToDelete.time)} ${getTimezoneAbbreviation(slotToDelete.timezone)}`}
            </Text>
            ? The deletion is staged and applied when you click Save Changes.
          </Text>

          {!deleteUsageInfo.canDelete && (
            <Alert color="red" icon={<AlertTriangle size={16} />}>
              This time slot cannot be deleted because it is assigned to a
              scheduled session. Please unassign the time slot from the group
              first.
            </Alert>
          )}

          {deleteUsageInfo.canDelete &&
            deleteUsageInfo.availabilityRequestCount > 0 && (
              <Alert color="yellow" icon={<AlertTriangle size={16} />}>
                This time slot has been included in{' '}
                {deleteUsageInfo.availabilityRequestCount} availability request
                {deleteUsageInfo.availabilityRequestCount > 1 ? 's' : ''}.
              </Alert>
            )}

          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={handleCloseDeleteModal}>
              Cancel
            </Button>
            <Button
              color="red"
              onClick={handleStageDelete}
              disabled={!deleteUsageInfo.canDelete}
            >
              Delete
            </Button>
          </Group>
        </Stack>
      ) : null}
    </Modal>
  );

  if (!isCollapsible) {
    return (
      <>
        <Stack gap="md">
          <Text fw={600}>Session Time Slot Options</Text>
          {content}
        </Stack>
        {deleteModal}
      </>
    );
  }

  return (
    <>
      <Stack gap="xs">
        <Group
          justify="space-between"
          style={{ cursor: 'pointer' }}
          onClick={() => setExpanded(!expanded)}
        >
          <Group gap="xs">
            <Clock size={18} />
            <Text fw={600}>Session Time Slot Options</Text>
          </Group>
          <ActionIcon variant="subtle" size="sm">
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </ActionIcon>
        </Group>

        <Collapse in={expanded}>{content}</Collapse>
      </Stack>
      {deleteModal}
    </>
  );
}

export default TimeSlotSection;
